import { createNamespacedHelpers } from 'vuex';
import { DefineActions, DefineGetters, DefineMutations } from 'vuex-type-helper';
import { AppData, Getters, Mutations, Actions } from '@/models/App.model';

// Inital state
const state: AppData = {
  snackbar: {
    show: false,
    text: '',
    color: 'error',
    mode: '',
    timeout: Number(process.env.VUE_APP_SNACKBAR_TIMEOUT),
    x: '',
    y: 'top',
    icon: '',
    closeIcon: 'mdi-close-circle-outline',
  },
  drawer: null,

  genders: [],
  races: [],
  locations: [],
  statuses: [],
  marks: [],
  subjects: [],
  qualifications: [],
  aspects: [],
  companies: [],
  careerCategories: [],
  careerSubCategories: [],
  classifications: [],
  careers: [],
};

// Gets the values of our state usually called from components
const getters: DefineGetters<Getters, AppData> = {
  snackbar: (state) => state.snackbar,
  drawer: (state) => state.drawer,
  genders: (state) => state.genders,
  races: (state) => state.races,
  locations: (state) => state.locations,
  statuses: (state) => state.statuses,
  marks: (state) => state.marks,
  subjects: (state) => state.subjects,
  qualifications: (state) => state.qualifications,
  aspects: (state) => state.aspects,
  companies: (state) => state.companies,
  careerCategories: (state) => state.careerCategories,
  careerSubCategories: (state) => state.careerSubCategories,
  classifications: (state) => state.classifications,
  careers: (state) => state.careers,

  version: (state) => {
    return process.env.VUE_APP_COPYRIGHT + ' ' + process.env.VUE_APP_VERSION_NUMBER;
  },

  urls: (state) => {
    return {
      login: process.env.VUE_APP_URL_LOGIN,
    };
  },

  icons: (state) => {
    return {
      // --- generic ---
        // -- informational
        date: 'mdi-calendar-outline',
        email: 'mdi-email',
        error: 'mdi-alert',
        favourite: 'mdi-star',
        info: 'mdi-information',
        infoOutline: 'mdi-information-outline',
        inProgress: 'mdi-progress-clock',
        no: 'mdi-close-thick',
        password: 'mdi-lock',
        pdf: 'mdi-file-pdf-box',
        profile: 'mdi-account',
        settings: 'mdi-cog',
        success: 'mdi-check-circle',
        time: 'mdi-clock',
        username: 'mdi-account',
        warning: 'mdi-bell',
        yes: 'mdi-check-bold',
        // --- actions ---
        activate: 'mdi-play',
        close: 'mdi-close-circle-outline',
        contract: 'mdi-menu-up',
        copy: 'mdi-content-copy',
        delete: 'mdi-delete',
        download: 'mdi-download',
        edit: 'mdi-pencil',
        expand: 'mdi-menu-down',
        filter: 'mdi-filter-variant',
        login: 'mdi-fingerprint',
        next: 'mdi-chevron-right',
        previous: 'mdi-chevron-left',
        print: 'mdi-printer',
        refresh: 'mdi-refresh-circle',
        register: 'mdi-account-plus',
        save: 'mdi-content-save',
        search: 'mdi-magnify',
        suspend: 'mdi-pause',
        view: 'mdi-eye',
        
      // --- app specific ---
        // --- informational ---
        confirmPassword: 'mdi-lock-check',
        ownYourCareer: 'mdi-briefcase',
        rotateDevice: 'mdi-phone-rotate-landscape',
        unlimitedAccess: 'mdi-lock-open-check',
        noAnswer: 'mdi-arrow-left-bold',
        hasAnswer: 'mdi-check-circle',
        // --- actions ---
        // --- nav ---
        dashboard: 'mdi-monitor-dashboard',
        library: 'mdi-bookshelf',
        drawerTools: 'mdi-hammer-screwdriver',
        generalTools: 'mdi-hammer-wrench',
        learners: 'mdi-book-open-variant',
        students: 'mdi-school',
        employed: 'mdi-briefcase',
        unemployed: 'mdi-briefcase-off',
        subjects: 'mdi-book-open-variant',
        institution: 'mdi-city-variant',
        qualification: 'mdi-script-text-outline',
        // --- profile nav ---
        // --- test ---
        stronglyDisagree: 'mdi-emoticon-frown-outline',
        disagree: 'mdi-emoticon-sad-outline',
        neutral: 'mdi-emoticon-neutral-outline',
        agree: 'mdi-emoticon-happy-outline',
        stronglyAgree: 'mdi-emoticon-outline',
        // --- additional ---        
    };
  },

  messages: (state) => {
    return {
      couldNotConnect: 'Error: Please contact Online Career Guidance Support for assistance.',
      sessionTimeout: 'Your session has timed out, please login to continue.',
    };
  },

  // https://codeburst.io/vuex-getters-are-great-but-dont-overuse-them-9c946689b414
  // from what I gather, in order to make a getter update itself, it needs to be parameterised
  // this was not working previously using getters on the actual vue files, even when they were
  // calling a method in AppService. They would not refresh on each call.
  // because we need this to be a generic global method, I have written it here as a getter.
  autoId: (state) => (random: string): string => {
    let autoId: string = ''; 
    const dict: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'; 
    
    for (let i = 0; i < 12; i++) { 
      autoId += dict.charAt(Math.floor(Math.random() * dict.length)); 
    } 

    return autoId;
  },

  maxWidthButton: (state) => (currentBreakpoint: string, maxWidthBreakpoints: string[]): string => {
    let returnValue = '';
    for (const breakpoint of maxWidthBreakpoints) {
      if (currentBreakpoint === breakpoint) {
        returnValue = '100%';
        break;
      }
    }

    return returnValue;
  },
};

// Sets values or changes to state. Must be called via an action.
const mutations: DefineMutations<Mutations, AppData> = {
  reset(state) {
    state.snackbar = {
      show: false,
      text: '',
      color: 'error',
      mode: '',
      timeout: 6000,
      x: '',
      y: 'top',
      icon: '',
      closeIcon: 'mdi-close-circle-outline',
    };

    state.drawer = null;
  },

  snackbar(state, { show, text, color, icon }) {
    state.snackbar.show = show;
    state.snackbar.text = text;
    state.snackbar.color = color;
    state.snackbar.icon = icon;
  },

  SET_DRAWER(state, payload) {
    state.drawer = payload;
  },

  setGenders(state, { genders }) {
    state.genders = genders;
  },

  setRaces(state, { races }) {
    state.races = races;
  },

  setLocations(state, { locations }) {
    state.locations = locations;
  },

  setStatuses(state, { statuses }) {
    state.statuses = statuses;
  },

  setMarks(state, { marks }) {
    state.marks = marks;
  },

  setSubjects(state, { subjects }) {
    state.subjects = subjects;
  },

  setQualifications(state, { qualifications }) {
    state.qualifications = qualifications;
  },

  setAspects(state, { aspects }) {
    state.aspects = aspects;
  },

  setCompanies(state, { companies }) {
    state.companies = companies;
  },

  setCareerCategories(state, {careerCategories}) {
    state.careerCategories = careerCategories;
  },

  setCareerSubCategories(state, {careerSubCategories}) {
    state.careerSubCategories = careerSubCategories;
  },

  setClassifications(state, {classifications}) { 
    state.classifications = classifications;
  },

  setCareers(state, {careers}) { 
    state.careers = careers;
  },
};

// Async functions that will contain the logic / service calls and commit mutations to state
const actions: DefineActions<Actions, AppData, Mutations, Getters> = {
  reset({ commit }, payload) {
    commit('reset', payload);
  },

  snackbar({ commit }, payload) {
    commit('snackbar', payload);
  },

  SET_DRAWER({ commit }, payload) {
    commit('SET_DRAWER', payload);
  },

  setGenders({ commit }, payload) {
    commit('setGenders', payload);
  },

  setRaces({ commit }, payload) {
    commit('setRaces', payload);
  },

  setLocations({ commit }, payload) {
    commit('setLocations', payload);
  },

  setStatuses({ commit }, payload) {
    commit('setStatuses', payload);
  },

  setMarks({ commit }, payload) {
    commit('setMarks', payload);
  },

  setSubjects({ commit }, payload) {
    commit('setSubjects', payload);
  },

  setQualifications({ commit }, payload) {
    commit('setQualifications', payload);
  },

  setAspects({ commit }, payload) {
    commit('setAspects', payload);
  },

  setCompanies({ commit }, payload) {
    commit('setCompanies', payload);
  },

  setCareerCategories({ commit }, payload) {
    commit('setCareerCategories', payload);
  },

  setCareerSubCategories({ commit }, payload) {
    commit('setCareerSubCategories', payload);
  },

  setClassifications({ commit }, payload) {
    commit('setClassifications', payload);
  },

  setCareers({ commit }, payload) {
    commit('setCareers', payload);
  },
};

export const {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} = createNamespacedHelpers<AppData, Getters, Mutations, Actions>(
  'AppModule',
);

export const AppModule = {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
};
